import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import {
  Bookmark, BookmarkX, Search, FileText, Loader2,
  User, MapPin, Briefcase, Clock, DollarSign, ArrowLeft, LogOut
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

export default function MyShortlist() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [shortlist, setShortlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeLoading, setRemoveLoading] = useState({});
  const [hireLoading, setHireLoading] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await companyAPI.getShortlist();
      setShortlist(res.data.data || []);
    } catch { toast.error('Failed to load shortlist'); }
    finally { setLoading(false); }
  };

  const handleRemove = async (staffId) => {
    setRemoveLoading((p) => ({ ...p, [staffId]: true }));
    try {
      await companyAPI.removeShortlist(staffId);
      setShortlist((p) => p.filter((s) => s.staffId !== staffId));
      toast.success('Removed from shortlist');
    } catch { toast.error('Failed to remove'); }
    finally { setRemoveLoading((p) => ({ ...p, [staffId]: false })); }
  };

  const handleHire = async (staffId) => {
    setHireLoading((p) => ({ ...p, [staffId]: true }));
    try {
      await companyAPI.createHireRequest({ staffId, message: '' });
      toast.success('Hire request sent!');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to send hire request'); }
    finally { setHireLoading((p) => ({ ...p, [staffId]: false })); }
  };

  const availLabel = (v) => ({ FULL_TIME:'Full Time', PART_TIME:'Part Time', CONTRACT:'Contract', FREELANCE:'Freelance' }[v] || v);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 transition p-1 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/company/search')}
              className="flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-800">
              <Search className="h-4 w-4" /> Search Staff
            </button>
            <button onClick={() => { logout(); navigate('/company/login'); }}
              className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Bookmark className="h-6 w-6 text-teal-600" />
          <h2 className="text-xl font-bold text-slate-800">My Shortlist</h2>
          <span className="px-2.5 py-0.5 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
            {shortlist.length}
          </span>
        </div>

        {shortlist.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <Bookmark className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-500">No staff shortlisted yet</h3>
            <p className="text-sm text-gray-400 mt-1">Browse staff and bookmark candidates you're interested in</p>
            <button onClick={() => navigate('/company/search')}
              className="mt-5 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 transition shadow-lg shadow-teal-500/20">
              Search Staff
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {shortlist.map((item) => (
              <div key={item.shortlistId}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">

                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl
                      flex items-center justify-center text-white font-black text-base shadow-md shadow-teal-500/20">
                      {item.firstName?.charAt(0)}{item.lastName?.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 leading-tight">
                        {item.firstName} {item.lastName}
                      </h3>
                      <p className="text-xs text-teal-600 font-semibold mt-0.5">{item.staffTypeName}</p>
                    </div>
                  </div>
                  <button onClick={() => handleRemove(item.staffId)}
                    disabled={removeLoading[item.staffId]}
                    className="text-red-400 hover:text-red-600 transition disabled:opacity-50 p-1">
                    {removeLoading[item.staffId]
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <BookmarkX className="h-5 w-5" />}
                  </button>
                </div>

                {/* Details */}
                <div className="space-y-1.5 text-sm">
                  {item.domainName && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Briefcase className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {item.domainName}
                    </div>
                  )}
                  {item.qualification && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {item.qualification} · {item.experienceYears} yr{item.experienceYears !== 1 ? 's' : ''} exp
                    </div>
                  )}
                  {item.city && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {item.city}{item.state ? `, ${item.state}` : ''}
                    </div>
                  )}
                  {item.availabilityType && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {availLabel(item.availabilityType)}
                    </div>
                  )}
                  {item.expectedSalaryRange && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <DollarSign className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {item.expectedSalaryRange}
                    </div>
                  )}
                </div>

                {/* Action */}
                <button onClick={() => handleHire(item.staffId)}
                  disabled={hireLoading[item.staffId]}
                  className="mt-auto w-full bg-teal-600 text-white py-2.5 rounded-xl text-sm font-bold
                    hover:bg-teal-700 transition flex items-center justify-center gap-2 disabled:opacity-60
                    shadow-lg shadow-teal-500/20">
                  {hireLoading[item.staffId]
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                    : <><FileText className="h-4 w-4" /> Send Hire Request</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}